import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  catalogueApi,
  clientsApi,
  projectsApi,
  quotationsApi,
  settingsApi,
  type QuotationCopyNotes,
  type QuotationDetail,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  BilingualTextField,
  DateField,
  ErrorState,
  FormSection,
  LoadingState,
  NumberField,
  Panel,
  RadioField,
  SegmentedControl,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
  newRowKey,
  patchRow,
  withoutRow,
  FormPage,
  useLoadOnce,
} from '../../components/ui/index.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { optionalText } from '../clients/bilingual.js';

/**
 * Create or edit a quotation.
 *
 * A QUOTATION MAY BE WRITTEN FOR SOMEBODY WITH NO CLIENT FILE. That is an
 * ordinary event at a reception desk, and refusing until the file exists would
 * put paperwork ahead of the work. The prospect fields are the same fields the
 * client file holds, so acceptance converts them without anyone retyping
 * anything.
 *
 * A DRAFT IS FULLY EDITABLE (D208): who it is addressed to (a client on file
 * or a prospect, either way round), the project, contact and location, every
 * paragraph, the services, and both discounts — each a percentage OR a fixed
 * amount. Clearing a discount sends `null`, so the clearing reaches the server.
 * Once the quotation has left draft only its internal notes still save; a
 * change after that is a new quotation created from it (D209).
 *
 * PRICES ARE RESOLVED BY THE SERVER — the client's agreement first, then the
 * catalogue. The unit-price field is left EMPTY by default for exactly that
 * reason, and filling it in is an override that needs the permission and a
 * reason. This page collects the reason rather than letting the user discover
 * the refusal after typing everything else.
 *
 * A FAILED SAVE KEEPS EVERYTHING (D205). The form is filled from the record
 * once (`useLoadOnce`); nothing on a failure resets it, and `FormPage` points
 * at the first field the server named.
 */

type DiscountKind = 'percent' | 'amount';

interface LineDraft {
  /** The row's own key — never its position (see components/ui/rows.ts). */
  readonly key: string;
  catalogueItemId: string | null;
  quantity: number | null;
  /** Empty means "use the resolved price". Never 0. */
  unitPrice: number | null;
  priceOverrideReason: string;
  discountKind: DiscountKind;
  /** A percentage, or an amount in major units. Empty means no discount. */
  discountValue: number | null;
  notes: string;
}

const emptyLine = (): LineDraft => ({
  key: newRowKey('quotation-line'),
  catalogueItemId: null,
  quantity: 1,
  unitPrice: null,
  priceOverrideReason: '',
  discountKind: 'percent',
  discountValue: null,
  notes: '',
});

/** Money typed in major units, as minor units. Empty stays empty. */
const toMinor = (value: number | null): number | null =>
  value === null ? null : Math.round(value * 100);

/** A discount as the API takes it: one of the two, the other `null`. */
function discountFields(kind: DiscountKind, value: number | null) {
  return {
    discountPercent: kind === 'percent' ? value : null,
    discountAmountMinor: kind === 'amount' ? toMinor(value) : null,
  };
}

/** A stored discount, as the form edits it. */
function discountDraft(percent: number | null, amountMinor: number | null) {
  return amountMinor !== null
    ? { kind: 'amount' as const, value: amountMinor / 100 }
    : { kind: 'percent' as const, value: percent };
}

export function QuotationFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const isNew = id === undefined;
  const canEdit = can(
    isNew ? PERMISSIONS.QUOTATIONS_CREATE : PERMISSIONS.QUOTATIONS_UPDATE,
  );
  const canOverride = can(PERMISSIONS.PRICING_OVERRIDE);

  /** What "Create New From This Quotation" reported, when it brought us here. */
  const copyNotes =
    (location.state as { copyNotes?: QuotationCopyNotes } | null)?.copyNotes ?? null;

  const [recipient, setRecipient] = useState<string>('client');
  const [clientId, setClientId] = useState<string | null>(
    searchParams.get('clientId'),
  );
  const [prospectName, setProspectName] = useState('');
  const [prospectContactName, setProspectContactName] = useState('');
  const [prospectContactTitle, setProspectContactTitle] = useState('');
  const [prospectEmail, setProspectEmail] = useState('');
  const [prospectPhone, setProspectPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [discountKind, setDiscountKind] = useState<DiscountKind>('percent');
  const [discountValue, setDiscountValue] = useState<number | null>(null);
  const [discountReason, setDiscountReason] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  // What the document prints beyond its lines (D200). The contact, project
  // and location are CHOSEN from records on file, never typed again.
  const [contactId, setContactId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectLocationId, setProjectLocationId] = useState<string | null>(null);
  const [inquiryReference, setInquiryReference] = useState('');
  const [introduction, setIntroduction] = useState({ ar: '', en: '' });
  const [paymentTerms, setPaymentTerms] = useState({ ar: '', en: '' });
  const [conditions, setConditions] = useState({ ar: '', en: '' });
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [remarks, setRemarks] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const quotation = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => quotationsApi.get(id as string),
    enabled: !isNew,
  });

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const forClient = recipient === 'client' && Boolean(clientId);
  const contacts = useQuery({
    queryKey: ['client-contacts', clientId, 'active'],
    queryFn: () => clientsApi.listContacts(clientId as string, false),
    enabled: forClient,
  });
  const projects = useQuery({
    queryKey: ['projects', 'for-client', clientId],
    queryFn: () => projectsApi.list({ clientId: clientId as string, pageSize: 100 }),
    enabled: forClient,
  });
  const locations = useQuery({
    queryKey: ['project-locations', projectId, 'active'],
    queryFn: () => projectsApi.listLocations(projectId as string, false),
    enabled: forClient && Boolean(projectId),
  });

  const catalogue = useQuery({
    queryKey: ['catalogue', 'selectable'],
    queryFn: () => catalogueApi.list({ pageSize: 100, includeInactive: false }),
  });

  /**
   * The laboratory's own settings, read so the default validity period can be
   * SHOWN rather than restated. The interface never carries a copy of it
   * (CLAUDE.md section 6.1).
   */
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
  });

  useLoadOnce(quotation.data, (record) => {
    setRecipient(record.party.kind);
    setClientId(record.party.clientId);
    // A client's own details live on its file; only a prospect's are typed here.
    const prospect = record.party.kind === 'prospect';
    setProspectName(prospect ? (record.party.name ?? '') : '');
    setProspectContactName(prospect ? (record.party.contactName ?? '') : '');
    setProspectContactTitle(prospect ? (record.party.contactTitle ?? '') : '');
    setProspectEmail(prospect ? (record.party.contactEmail ?? '') : '');
    setProspectPhone(prospect ? (record.party.contactPhone ?? '') : '');
    setContactId(record.contact?.id ?? null);
    setProjectId(record.projectId);
    setProjectLocationId(record.projectLocation?.id ?? null);
    setInquiryReference(record.inquiryReference ?? '');
    setIntroduction({ ar: record.introduction.ar ?? '', en: record.introduction.en ?? '' });
    setPaymentTerms({ ar: record.paymentTerms.ar ?? '', en: record.paymentTerms.en ?? '' });
    setConditions({ ar: record.terms.ar ?? '', en: record.terms.en ?? '' });
    setScopeOfWork(record.scopeOfWork ?? '');
    setRemarks(record.remarks ?? '');
    setAdditionalNotes(record.additionalNotes ?? '');
    setSubject(record.subject ?? '');
    setValidUntil(record.validUntil);
    const discount = discountDraft(record.discountPercent, record.discountAmountMinor ?? null);
    setDiscountKind(discount.kind);
    setDiscountValue(discount.value);
    setDiscountReason(record.discountReason ?? '');
    setNotes(record.notes ?? '');
    setLines(
      record.lines.length > 0
        ? record.lines.map((line) => {
            const lineDiscount = discountDraft(line.discountPercent, line.discountAmountMinor ?? null);
            return {
              key: newRowKey('quotation-line'),
              catalogueItemId: line.catalogueItemId,
              quantity: line.quantity,
              // Only shown as a typed price when it really was an override.
              unitPrice:
                line.priceSource === 'override' ? line.unitPriceMinor / 100 : null,
              priceOverrideReason: line.priceOverrideReason ?? '',
              discountKind: lineDiscount.kind,
              discountValue: lineDiscount.value,
              notes: line.notes ?? '',
            };
          })
        : [emptyLine()],
    );
  });

  const isDeleted = quotation.data?.isDeleted ?? false;
  const isEditable = isNew || (quotation.data?.isEditable ?? false);

  const linePayload = () =>
    lines
      .filter((line) => line.catalogueItemId && line.quantity)
      .map((line) => ({
        catalogueItemId: line.catalogueItemId,
        quantity: line.quantity as number,
        // Absent unless somebody actually typed one. An empty field must not
        // become a price of zero.
        ...(line.unitPrice === null
          ? {}
          : { unitPriceMinor: toMinor(line.unitPrice) }),
        ...(line.priceOverrideReason.trim()
          ? { priceOverrideReason: line.priceOverrideReason.trim() }
          : {}),
        // Always sent: a cleared discount is `null`, and reaches the server.
        ...discountFields(line.discountKind, line.discountValue),
        notes: optionalText(line.notes),
      }));

  const save = useMutation({
    mutationFn: async (): Promise<QuotationDetail> => {
      if (!isNew && !isEditable) {
        // Left draft: only the internal notes, which are never printed (D208).
        return quotationsApi.update(id as string, { notes: optionalText(notes) });
      }

      const prospect = {
        name: prospectName.trim(),
        contactName: optionalText(prospectContactName),
        contactTitle: optionalText(prospectContactTitle),
        contactEmail: optionalText(prospectEmail),
        contactPhone: optionalText(prospectPhone),
      };
      const shared = {
        subject: optionalText(subject),
        ...(validUntil ? { validUntil } : {}),
        ...discountFields(discountKind, discountValue),
        notes: optionalText(notes),
        inquiryReference: optionalText(inquiryReference),
        scopeOfWork: optionalText(scopeOfWork),
        remarks: optionalText(remarks),
        additionalNotes: optionalText(additionalNotes),
        ...(recipient === 'client'
          ? { contactId, projectId, projectLocationId: projectId ? projectLocationId : null }
          : {}),
      };

      if (isNew) {
        // The introduction, payment terms and conditions are NOT sent: the
        // server copies the laboratory's current wording when it creates the
        // quotation, and the form shows them for editing from then on.
        return quotationsApi.create({
          ...shared,
          ...(discountReason.trim() ? { discountReason: discountReason.trim() } : {}),
          ...(recipient === 'client' ? { clientId } : { prospect }),
          lines: linePayload(),
        });
      }

      const record = quotation.data;
      const updated = await quotationsApi.update(id as string, {
        ...shared,
        discountReason: optionalText(discountReason),
        // Who it is addressed to may change on a draft (D208). A prospect's
        // details this form does not show are sent back as they are, so
        // re-saving never erases them.
        ...(recipient === 'client'
          ? { clientId }
          : {
              prospect: {
                ...prospect,
                ...(record?.party.kind === 'prospect'
                  ? {
                      vatRegistrationNumber: record.party.vatRegistrationNumber ?? null,
                      commercialRegistrationNumber:
                        record.party.commercialRegistrationNumber ?? null,
                      addressLine: record.party.addressLine ?? null,
                      city: record.party.city ?? null,
                      countryCode: record.party.countryCode ?? null,
                    }
                  : {}),
              },
            }),
        introduction: { ar: optionalText(introduction.ar), en: optionalText(introduction.en) },
        paymentTerms: { ar: optionalText(paymentTerms.ar), en: optionalText(paymentTerms.en) },
        terms: { ar: optionalText(conditions.ar), en: optionalText(conditions.en) },
      });
      // Lines are a separate, replace-the-set call — and the server refuses it
      // for anything but a draft, so this is attempted only where it is legal.
      if (updated.isEditable) {
        return quotationsApi.setLines(id as string, { lines: linePayload() });
      }
      return updated;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['quotations'] });
      await queryClient.invalidateQueries({ queryKey: ['quotation', saved.id] });
      show({ tone: 'success', message: t('quotations.saved') });
      navigate(`/quotations/${saved.id}`, { replace: true });
    },
  });

  if (!isNew && quotation.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  if (!isNew && quotation.error) {
    return (
      <ErrorState
        error={quotation.error}
        onRetry={() => void quotation.refetch()}
      />
    );
  }

  const currency = quotation.data?.currency ?? settings.data?.currency;
  const money = (minor: number) =>
    formatMoney(minor, { locale, currency: currency ?? '' }) ?? String(minor / 100);

  /** The copy notes, as sentences: every difference named (D209). */
  const copySentences = copyNotes
    ? [
        ...copyNotes.repricedLines.map((line) =>
          t(`quotations.copy.repriced.${line.reason}`, {
            line: line.lineNumber,
            code: line.itemCode ?? '',
            from: money(line.sourceUnitPriceMinor),
            to: money(line.unitPriceMinor),
          }),
        ),
        ...copyNotes.skippedLines.map((line) =>
          t(`quotations.copy.skipped.${line.reason}`, {
            line: line.lineNumber,
            code: line.itemCode ?? '',
          }),
        ),
        ...copyNotes.droppedReferences.map((reference) =>
          t(`quotations.copy.dropped.${reference}`),
        ),
      ]
    : [];

  const chooseRecipient = (value: string) => {
    setRecipient(value);
    // What belonged to one party does not carry over to another.
    setContactId(null);
    setProjectId(null);
    setProjectLocationId(null);
  };

  return (
    <FormPage
      title={
        isNew ? t('quotations.createTitle') : t('quotations.editTitle')
      }
      breadcrumbs={[
        { label: t('nav.quotations'), to: '/quotations' },
        {
          label: isNew
            ? t('quotations.createTitle')
            : quotation.data?.quotationNumber ?? t('quotations.editTitle'),
        },
      ]}
      onSubmit={() => save.mutate()}
      onCancel={() => navigate(isNew ? '/quotations' : `/quotations/${id}`)}
      isSubmitting={save.isPending}
      canSubmit={canEdit && !isDeleted}
      error={save.error}
      fieldLabels={{
        clientId: t('quotations.client'),
        subject: t('quotations.subject'),
        validUntil: t('quotations.validUntil'),
        discountPercent: t('quotations.discountPercent'),
        discountAmountMinor: t('quotations.discountAmount'),
        discountReason: t('quotations.discountReason'),
        contactId: t('quotations.contact'),
        projectId: t('quotations.project'),
        projectLocationId: t('quotations.location'),
      }}
      notices={
        <>
          {copyNotes && quotation.data?.sourceQuotation ? (
            <div className="mb-stack grid gap-2">
              <Alert
                tone={copySentences.length > 0 ? 'warning' : 'info'}
                title={t('quotations.copy.createdFrom', {
                  number: quotation.data.sourceQuotation.quotationNumber,
                })}
                description={
                  copySentences.length > 0
                    ? t('quotations.copy.differencesIntro')
                    : t('quotations.copy.noDifferences')
                }
              />
              {copySentences.length > 0 ? (
                <ul
                  aria-label={t('quotations.copy.differencesLabel')}
                  className="list-disc ps-6 text-body text-default-800"
                >
                  {copySentences.map((sentence) => (
                    <li key={sentence}>{sentence}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {isDeleted ? (
            <div className="mb-stack">
              <Alert tone="warning" title={t('quotations.deleted.banner')} />
            </div>
          ) : !isEditable ? (
            <div className="mb-stack">
              <Alert tone="warning" title={t('quotations.lockedWarning')} />
            </div>
          ) : null}
        </>
      }
    >
        {/* 1. Client and project */}
        <FormSection title={t('quotations.sectionClientProject')} columns={1}>
          {isEditable ? (
            <RadioField
              label={t('quotations.recipient')}
              // The section's legend already says it.
              isLabelHidden
              value={recipient}
              onChange={chooseRecipient}
              options={[
                {
                  value: 'client',
                  label: t('quotations.registeredClient'),
                },
                {
                  value: 'prospect',
                  label: t('quotations.unregisteredClient'),
                  description: t('quotations.prospectHint'),
                },
              ]}
            />
          ) : null}

          {recipient === 'client' ? (
            <SelectField
              label={t('quotations.client')}
              placeholder={t('quotations.selectClient')}
              value={clientId}
              onChange={(value) => {
                setClientId(value);
                // A contact or project of the previous client does not carry over.
                setContactId(null);
                setProjectId(null);
                setProjectLocationId(null);
              }}
              isRequired
              isDisabled={!isEditable}
              {...(!isNew && isEditable ? { description: t('quotations.clientChangeHint') } : {})}
              options={[
                ...(clients.data?.data ?? []).map((client) => ({
                  value: client.id,
                  label: client.name || client.clientNumber,
                  description: client.clientNumber,
                })),
                // The client on the record, even when the list would not
                // offer it (suspended since), so the field is never blank.
                ...(quotation.data?.party.kind === 'client' &&
                quotation.data.party.clientId &&
                !(clients.data?.data ?? []).some((client) => client.id === quotation.data?.party.clientId)
                  ? [
                      {
                        value: quotation.data.party.clientId,
                        label: quotation.data.party.name,
                        description: quotation.data.party.clientNumber ?? '',
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <>
              <TextField
                label={t('quotations.prospectName')}
                value={prospectName}
                onChange={setProspectName}
                textDirection="auto"
                isRequired
                isReadOnly={!isEditable}
              />
              <TextField
                label={t('quotations.prospectContactName')}
                value={prospectContactName}
                onChange={setProspectContactName}
                isReadOnly={!isEditable}
              />
              <TextField
                label={t('quotations.prospectContactTitle')}
                value={prospectContactTitle}
                onChange={setProspectContactTitle}
                textDirection="auto"
                isReadOnly={!isEditable}
              />
              <TextField
                label={t('quotations.prospectEmail')}
                type="email"
                value={prospectEmail}
                onChange={setProspectEmail}
                isReadOnly={!isEditable}
              />
              <TextField
                label={t('quotations.prospectPhone')}
                type="tel"
                value={prospectPhone}
                onChange={setProspectPhone}
                isReadOnly={!isEditable}
              />
            </>
          )}

          {forClient ? (
            <div className="grid min-w-0 gap-stack sm:grid-cols-3">
              <SelectField
                label={t('quotations.contact')}
                placeholder={t('quotations.noContactChosen')}
                value={contactId}
                onChange={setContactId}
                isLoading={contacts.isLoading}
                isDisabled={!isEditable}
                options={(contacts.data ?? []).map((contact) => ({
                  value: contact.id,
                  label: contact.name,
                  ...(contact.position || contact.email
                    ? { description: [contact.position, contact.email].filter(Boolean).join(' · ') }
                    : {}),
                }))}
              />
              <SelectField
                label={t('quotations.project')}
                placeholder={t('quotations.noProjectChosen')}
                value={projectId}
                onChange={(value) => {
                  setProjectId(value);
                  setProjectLocationId(null);
                }}
                isLoading={projects.isLoading}
                isDisabled={!isEditable}
                options={(projects.data?.data ?? []).map((project) => ({
                  value: project.id,
                  label: project.name,
                  ...(project.reference ? { description: project.reference } : {}),
                }))}
              />
              <SelectField
                label={t('quotations.location')}
                placeholder={t('quotations.noLocationChosen')}
                value={projectLocationId}
                onChange={setProjectLocationId}
                isLoading={locations.isLoading && Boolean(projectId)}
                isDisabled={!isEditable || !projectId}
                options={(locations.data ?? []).map((location) => ({
                  value: location.id,
                  label: location.name,
                  ...(location.city ? { description: location.city } : {}),
                }))}
              />
            </div>
          ) : null}
        </FormSection>

        {/* 2. Proposal details */}
        <FormSection title={t('quotations.sectionProposal')}>
          <TextField
            label={t('quotations.subject')}
            value={subject}
            onChange={setSubject}
            textDirection="auto"
            isReadOnly={!isEditable}
          />
          <TextField
            label={t('quotations.inquiryReference')}
            description={t('quotations.inquiryReferenceHint')}
            value={inquiryReference}
            onChange={setInquiryReference}
            textDirection="auto"
            isReadOnly={!isEditable}
          />
          <DateField
            label={t('quotations.validUntil')}
            // The laboratory's OWN default, read from its settings and shown
            // as guidance. Never a number written into this file. Once sent,
            // the date is part of what the client holds (D208).
            description={
              isEditable ? t('quotations.validityHint') : t('quotations.validityLockedHint')
            }
            value={validUntil}
            onChange={setValidUntil}
            isDisabled={!isEditable}
          />
        </FormSection>

        <Panel
          title={t('quotations.lines')}
          actions={
            isEditable && canEdit ? (
              <ActionButton
                priority="secondary"
                size="sm"
                onPress={() => setLines((current) => [...current, emptyLine()])}
              >
                {t('quotations.addLine')}
              </ActionButton>
            ) : null
          }
        >
          <div className="grid gap-gutter">
            {lines.length === 0 ? (
              <p className="text-body text-default-600">{t('common.noLinesYet')}</p>
            ) : null}
            {lines.map((line, index) => {
              const item = catalogue.data?.data.find(
                (candidate) => candidate.id === line.catalogueItemId,
              );
              return (
                // One block per line: its number and its removal in a header
                // strip, the line's fields beneath (D278).
                <section
                  key={line.key}
                  aria-label={t('ordersRecord.lineNumber', { n: index + 1 })}
                  className="min-w-0 rounded-medium border border-default-200"
                >
                  <header className="flex min-h-10 items-center gap-inline border-b border-default-200 bg-default-50 px-3 py-1.5">
                    <span className="text-caption font-semibold text-default-800">
                      {t('ordersRecord.lineNumber', { n: index + 1 })}
                    </span>
                    {isEditable && canEdit ? (
                      <span className="ms-auto">
                        <ActionButton
                          priority="tertiary"
                          size="sm"
                          onPress={() =>
                            setLines((current) => withoutRow(current, line.key))
                          }
                        >
                          {t('quotations.removeLine')}
                        </ActionButton>
                      </span>
                    ) : null}
                  </header>
                  <div className="grid min-w-0 grid-cols-1 gap-stack p-3 sm:grid-cols-2">
                    <SelectField
                      label={t('quotations.selectService')}
                      value={line.catalogueItemId}
                      onChange={(value) =>
                        setLines((current) => patchRow(current, line.key, { catalogueItemId: value }))
                      }
                      isDisabled={!isEditable}
                      options={(catalogue.data?.data ?? []).map((entry) => ({
                        value: entry.id,
                        label:
                          (locale === 'ar' ? entry.name.ar : entry.name.en) ??
                          entry.code ??
                          '',
                        // An unpriced entry says so in the dropdown, so the desk
                        // knows a price will have to be typed before it saves.
                        description:
                          entry.basePriceMinor === null
                            ? t('catalogue.notPriced')
                            : (formatMoney(entry.basePriceMinor, {
                                locale,
                                currency: entry.currency,
                              }) ?? undefined),
                      }))}
                    />
                    <NumberField
                      label={t('quotations.quantity')}
                      value={line.quantity}
                      onChange={(value) =>
                        setLines((current) => patchRow(current, line.key, { quantity: value }))
                      }
                      step="0.001"
                      isReadOnly={!isEditable}
                    />
                    {canOverride ? (
                      <NumberField
                        label={t('quotations.unitPrice')}
                        description={t('quotations.unitPriceHint')}
                        value={line.unitPrice}
                        onChange={(value) =>
                          setLines((current) => patchRow(current, line.key, { unitPrice: value }))
                        }
                        step="0.01"
                        {...(currency ? { unit: currency } : {})}
                        isReadOnly={!isEditable}
                      />
                    ) : null}
                    {/*
                      The reason appears only once a price is actually typed, and
                      only where it differs from what the catalogue says. Asking
                      for one unconditionally would train people to write one.
                    */}
                    {canOverride &&
                    line.unitPrice !== null &&
                    item &&
                    toMinor(line.unitPrice) !== item.basePriceMinor ? (
                      <TextAreaField
                        label={t('quotations.priceOverrideReason')}
                        value={line.priceOverrideReason}
                        onChange={(value) =>
                          setLines((current) => patchRow(current, line.key, { priceOverrideReason: value }))
                        }
                        rows={2}
                        isRequired
                        isReadOnly={!isEditable}
                      />
                    ) : null}
                    <div className="grid min-w-0 gap-2">
                      <SegmentedControl<DiscountKind>
                        label={t('quotations.lineDiscountKind')}
                        value={line.discountKind}
                        onChange={(kind) =>
                          setLines((current) =>
                            patchRow(current, line.key, { discountKind: kind, discountValue: null }),
                          )
                        }
                        isDisabled={!isEditable}
                        options={[
                          { value: 'percent', label: t('quotations.discountAsPercent') },
                          { value: 'amount', label: t('quotations.discountAsAmount') },
                        ]}
                      />
                      <NumberField
                        label={
                          line.discountKind === 'percent'
                            ? t('quotations.lineDiscount')
                            : t('quotations.lineDiscountAmount')
                        }
                        value={line.discountValue}
                        onChange={(value) =>
                          setLines((current) => patchRow(current, line.key, { discountValue: value }))
                        }
                        step="0.01"
                        {...(line.discountKind === 'amount' && currency ? { unit: currency } : {})}
                        isReadOnly={!isEditable}
                      />
                  </div>
                  <TextAreaField
                    label={t('quotations.lineNotes')}
                    value={line.notes}
                    onChange={(value) =>
                      setLines((current) => patchRow(current, line.key, { notes: value }))
                    }
                    rows={2}
                    textDirection="auto"
                    isReadOnly={!isEditable}
                  />
                  </div>
                </section>
              );
            })}
          </div>
        </Panel>

        <FormSection
          title={t('quotations.discount')}
          description={t('quotations.discountHint')}
        >
          <div className="grid min-w-0 gap-2">
            <SegmentedControl<DiscountKind>
              label={t('quotations.discountKind')}
              value={discountKind}
              onChange={(kind) => {
                setDiscountKind(kind);
                setDiscountValue(null);
              }}
              isDisabled={!isEditable}
              options={[
                { value: 'percent', label: t('quotations.discountAsPercent') },
                { value: 'amount', label: t('quotations.discountAsAmount') },
              ]}
            />
            <NumberField
              label={
                discountKind === 'percent'
                  ? t('quotations.discountPercent')
                  : t('quotations.discountAmount')
              }
              value={discountValue}
              onChange={setDiscountValue}
              step="0.01"
              {...(discountKind === 'amount' && currency ? { unit: currency } : {})}
              isReadOnly={!isEditable}
            />
          </div>
          <TextAreaField
            label={t('quotations.discountReason')}
            value={discountReason}
            onChange={setDiscountReason}
            rows={2}
            isReadOnly={!isEditable}
          />
        </FormSection>

        {/* 4. Commercial terms — the quotation's own copy of the laboratory's wording */}
        <FormSection
          title={t('quotations.sectionCommercial')}
          description={
            isNew ? t('quotations.commercialCopiedOnSave') : t('quotations.commercialHint')
          }
          columns={1}
        >
          {isNew ? null : (
            <>
              <BilingualTextField
                labelAr={t('quotations.introductionAr')}
                labelEn={t('quotations.introductionEn')}
                valueAr={introduction.ar}
                valueEn={introduction.en}
                onChangeAr={(ar) => setIntroduction((current) => ({ ...current, ar }))}
                onChangeEn={(en) => setIntroduction((current) => ({ ...current, en }))}
                rows={3}
                isReadOnly={!isEditable}
              />
              <BilingualTextField
                labelAr={t('quotations.paymentTermsAr')}
                labelEn={t('quotations.paymentTermsEn')}
                valueAr={paymentTerms.ar}
                valueEn={paymentTerms.en}
                onChangeAr={(ar) => setPaymentTerms((current) => ({ ...current, ar }))}
                onChangeEn={(en) => setPaymentTerms((current) => ({ ...current, en }))}
                rows={2}
                isReadOnly={!isEditable}
              />
              <BilingualTextField
                labelAr={t('quotations.conditionsAr')}
                labelEn={t('quotations.conditionsEn')}
                valueAr={conditions.ar}
                valueEn={conditions.en}
                onChangeAr={(ar) => setConditions((current) => ({ ...current, ar }))}
                onChangeEn={(en) => setConditions((current) => ({ ...current, en }))}
                rows={5}
                isReadOnly={!isEditable}
              />
            </>
          )}
        </FormSection>

        {/* 5. Scope and remarks */}
        <FormSection title={t('quotations.sectionScope')} columns={1}>
          <TextAreaField
            label={t('quotations.scopeOfWork')}
            value={scopeOfWork}
            onChange={setScopeOfWork}
            rows={4}
            textDirection="auto"
            isReadOnly={!isEditable}
          />
          <TextAreaField
            label={t('quotations.remarks')}
            value={remarks}
            onChange={setRemarks}
            rows={3}
            textDirection="auto"
            isReadOnly={!isEditable}
          />
        </FormSection>

        {/* 6. Additional notes: printed for the client; the internal notes never are */}
        <FormSection title={t('quotations.sectionNotes')} columns={1}>
          <TextAreaField
            label={t('quotations.additionalNotes')}
            description={t('quotations.additionalNotesHint')}
            value={additionalNotes}
            onChange={setAdditionalNotes}
            rows={3}
            textDirection="auto"
            isReadOnly={!isEditable}
          />
          <TextAreaField
            label={t('quotations.notes')}
            description={t('quotations.notesHint')}
            value={notes}
            onChange={setNotes}
            rows={3}
            isReadOnly={isDeleted}
          />
        </FormSection>
    </FormPage>
  );
}
